import { DragEndEvent } from '@dnd-kit/core';
import dayjs from 'dayjs';
import {
  buildAllocateAppointmentChange,
  buildDailyChange,
  buildWeeklyChange,
  getPredictedTime,
} from 'tsx/features/weekly-planner/lib/simulate';
import { timeValueFormat } from 'tsx/libs/dayjs';

import { filterChangedValues } from '~libs/dataUtils';

import { simulate } from '~weekly-planner/actions/simulate';

import { makeChange } from '~weekly-planner/reducers/unsaved';

export const MAX_PIPS = 288;
export const PIP_INTERVALS = 5; // Each pip represents x number of minutes

function isOvernight(start: string, end: string) {
  return dayjs(end, timeValueFormat).diff(dayjs(start, timeValueFormat), 'minute') <= 0;
}

export const onWeeklyAppointmentDragEnd = (event: DragEndEvent, options?: any) => {
  const { active, over } = event;
  const { current } = active?.data ?? {};
  if (!over || !current) return;

  const { containerId: oldCell, appointment, data, startDate, dispatch } = current;

  const newCell = over.id;

  if (oldCell === newCell) return;

  const change = buildWeeklyChange(data, oldCell, newCell.toString(), startDate, appointment);
  const { id, parent_repeat_id, ...changedVals } = change.data;
  dispatch(
    makeChange({
      original: appointment,
      change: { id, parent_repeat_id, ...filterChangedValues(changedVals, appointment) },
    }),
  );
  dispatch(simulate({ ...change, week_start: startDate, options }));
};

export const onDailyAppointmentDragEnd = (event: any, options?: any) => {
  const { active, over, delta } = event;
  const { current } = active?.data ?? {};
  if (!over || !current) {
    return;
  }

  const { containerId: oldRow, appointment, data, startDate, dispatch } = current;
  const {
    id: newRow,
    rect: { width },
  } = over;

  // Time change event, changing start/end time
  let apptData = appointment;
  if (oldRow === newRow) {
    let { start_time, end_time, duration } = appointment;
    const { date } = appointment;
    const { x } = delta;

    if (!duration) {
      duration = dayjs(`${end_time}`, timeValueFormat).diff(dayjs(`${start_time}`, timeValueFormat), 'minute');
      if (duration <= 0) duration += 24 * 60;
    }

    const predictedConverter = (selTime: string) => getPredictedTime(width, x, MAX_PIPS, selTime, PIP_INTERVALS, date);

    const predictedStart = predictedConverter(start_time);
    let newStart = predictedStart.format(timeValueFormat);

    // we don't support dragging backwards to make ends_next_day as it's far too confusing
    if (predictedStart.isBefore(date, 'day')) {
      newStart = '00:00';
    }

    // Always calculate newEnd based on duration
    const newEnd = dayjs(`2000-01-01T${newStart}`).add(duration, 'minute').format(timeValueFormat);

    // If not moved, do not simulate.
    if (newStart === start_time && newEnd === end_time) {
      return;
    }

    // Only snap for non-overnight appointments
    if (!isOvernight(newStart, newEnd) && dayjs(newEnd, timeValueFormat).isAfter(dayjs('23:55', timeValueFormat))) {
      end_time = '23:55';
      start_time = dayjs(end_time, timeValueFormat).subtract(duration, 'minute').format(timeValueFormat);
    } else {
      start_time = newStart;
      end_time = newEnd;
    }

    apptData = {
      ...apptData,
      start_time,
      end_time,
    };
  }

  const change = buildDailyChange(data, oldRow, newRow.toString(), apptData);
  const { id, parent_repeat_id, ...changedVals } = change.data;
  dispatch(
    makeChange({
      original: appointment,
      change: { id, parent_repeat_id, ...filterChangedValues(changedVals, appointment) },
    }),
  );
  dispatch(simulate({ ...change, week_start: startDate, options }));
};

export const onDailyAppointmentDragging = (event: any) => {
  const { active, over, delta } = event;
  const { current } = active?.data ?? {};
  if (!over || !current) {
    return;
  }

  const { containerId: oldRow, appointment } = current;
  const {
    id: newRow,
    rect: { width },
  } = over;

  const { start_time, end_time } = appointment;
  const { date } = appointment;
  const { x } = delta;

  // If dragging on same row, Update time, otherwise return empty object.
  if (oldRow === newRow) {
    const predictedConverter = (selTime: string) => getPredictedTime(width, x, MAX_PIPS, selTime, PIP_INTERVALS, date);

    const newStart = predictedConverter(start_time).format(timeValueFormat);
    const newEnd = predictedConverter(end_time).format(timeValueFormat);

    return {
      id: appointment.id,
      start_time: newStart,
      end_time: newEnd,
    };
  }
  return {};
};

export const onUnallocatedDragEnd = (event: any, options?: any) => {
  const { active, over } = event;
  const { current } = active?.data ?? {};
  if (!over || !current) {
    return;
  }

  const { containerId: oldCell, appointment, weeklyPlannerData, weekStart, dispatch } = current;
  const newCell = over.id;
  if (oldCell === newCell) {
    return;
  }

  const change = buildAllocateAppointmentChange(weeklyPlannerData, newCell.toString(), appointment);

  const changes = Array.isArray(change.data) ? change.data : [change.data];
  changes.forEach(({ id, parent_repeat_id, user_id, date }) => {
    dispatch(
      makeChange({
        original: appointment,
        change: { id, parent_repeat_id, date, user_id },
      }),
    );
  });

  dispatch(simulate({ ...change, week_start: weekStart, options }));
};
